// Thin client for the altGetTours Lambda (API Gateway, us-east-2).
// Every call is a plain GET with simple headers, so no CORS preflight is needed.
import { formatPerformance, isUpcoming } from "../utils/tourTime";

const API_BASE =
  process.env.REACT_APP_TOURS_API ||
  "https://wlsj713nkb.execute-api.us-east-2.amazonaws.com/default/altGetTours";
const REQUEST_TIMEOUT_MS = 15000;

/** How many upcoming dates a tour card / booking panel shows. */
export const MAX_UPCOMING = 6;

async function apiGet(params) {
  const query = Object.keys(params)
    .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(params[k]))
    .join("&");
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : null;
  try {
    const res = await fetch(API_BASE + "?" + query, {
      signal: controller ? controller.signal : undefined,
      headers: { Accept: "application/json" },
    });
    if (!res.ok) throw new Error("Tour API responded with " + res.status);
    const body = await res.json();
    return Array.isArray(body) ? body : [];
  } finally {
    if (timer) clearTimeout(timer);
  }
}

const AVAILABILITY_CACHE_MS = 60000;
const availabilityCache = new Map();
let toursPromise = null;

/** Test hook: forget memoised responses so each test starts clean. */
export function resetApiCaches() {
  toursPromise = null;
  availabilityCache.clear();
}

/** All tours (enabled and disabled). Shared across the app: one request per page load. */
export function getTours() {
  if (!toursPromise) {
    toursPromise = apiGet({ type: "tour", slug: "all" }).catch((err) => {
      toursPromise = null; // let the next caller retry
      throw err;
    });
  }
  return toursPromise;
}

export function getTour(slug) {
  return apiGet({ type: "tour", slug });
}

export function getTourContent(slug) {
  return apiGet({ type: "content", slug });
}

export function getTourPhotos(slug) {
  return apiGet({ type: "photos", slug });
}

export function getTourGroups(slug) {
  return apiGet({ type: "groups", slug: slug || "all" });
}

function seatsLeft(row) {
  // ThunderTix marks a performance unavailable (sales closed, manually sold out) independently of the seat count.
  if (row.available === false || (row.sold_out_message && String(row.sold_out_message).trim())) return 0;
  if (row.seats_capacity === 0) return 0;
  const n = Number(row.remaining_seats);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Upcoming performances for a ThunderTix event, oldest first, capped at `limit`.
 * `future`/`limit` are honoured by the current Lambda and ignored by older ones,
 * so the client filters and caps again either way.
 */

function fetchAvailabilityRows(thundertixId, cap) {
  const key = thundertixId + ":" + cap;
  const hit = availabilityCache.get(key);
  if (hit && Date.now() - hit.at < AVAILABILITY_CACHE_MS) return hit.promise;
  const promise = apiGet({ type: "availability", thundertixid: thundertixId, future: 1, limit: cap }).catch((err) => {
    availabilityCache.delete(key);
    throw err;
  });
  availabilityCache.set(key, { at: Date.now(), promise });
  return promise;
}

export async function getTourAvailability(thundertixId, limit) {
  const cap = limit || MAX_UPCOMING;
  if (!thundertixId) return [];
  const rows = await fetchAvailabilityRows(thundertixId, cap);
  const upcoming = [];
  for (const row of rows) {
    if (!row || !row.time || !isUpcoming(row.time)) continue;
    const when = formatPerformance(row.time);
    if (!when) continue;
    upcoming.push({ ...when, availability: seatsLeft(row), performanceId: row.id });
  }
  upcoming.sort((a, b) => a.sortKey - b.sortKey);
  return upcoming.slice(0, cap);
}
